import * as WindowManagement from "@omni/services/windowManagement";
import { WindowKeys } from "@omni/services/windowManagement";
import { AnalyticsEvent } from "@omni/types/components/tracking/analyticsEvent";
import { Mediator } from "mediator-js";

import { EnrichedAnalyticsEvent } from "../analytics.types";
import { deepCopy } from "../util/DeepCopy";
import { enrichEvent } from "../util/enrich";

export type Subscriber = {
    name: string;
    id: string;
    fn: Function;
    channel: string;
};

export type SubscriberCallback = (
    eventName: string,
    event: EnrichedAnalyticsEvent,
    context?: any
) => void;

export type TMediator = {
    subscribe: (
        channel: string,
        callback: SubscriberCallback,
        options?: any
    ) => Omit<Subscriber, "name">;
    publish: (
        channel: string,
        eventName: string,
        event: EnrichedAnalyticsEvent,
        context?: any
    ) => void;
    remove: (channel: string, id: string) => void;
};

const NAMESPACE_TRACKING = "tracking";
const NAMESPACE_HISTORY = "tracking_history";

class EventDataLayer {
    private mediator: TMediator;
    private trackingHistory: EnrichedAnalyticsEvent[];
    public trackingSubscribers: Subscriber[];

    constructor() {
        this.trackingSubscribers = [];
        this.trackingHistory = [];
        this.mediator = new Mediator();
    }

    public static getInstance(): EventDataLayer {
        if (!WindowManagement.getObject(WindowKeys.TRACKER)) {
            WindowManagement.saveObject(
                WindowKeys.TRACKER,
                new EventDataLayer()
            );
        }
        return WindowManagement.getObject(WindowKeys.TRACKER);
    }

    public clear() {
        this.trackingSubscribers.forEach((subscriber) =>
            this.mediator.remove(NAMESPACE_TRACKING, subscriber.id)
        );
        this.trackingSubscribers = [];
    }

    public subscribe(
        subscriberName: string,
        callback: SubscriberCallback,
        withHistory: boolean = false
    ) {
        if (!subscriberName || typeof callback !== "function") {
            throw new Error(`Invalid subscriber`);
        }

        this.trackingSubscribers.push({
            name: subscriberName,
            ...this.mediator.subscribe(NAMESPACE_TRACKING, callback)
        });

        if (withHistory) {
            this.runHistory(callback);
        }
    }

    public track(event: AnalyticsEvent) {
        const trackingEvent = deepCopy(event);
        const enrichtedEvent = enrichEvent(trackingEvent);
        this.mediator.publish(
            NAMESPACE_TRACKING,
            trackingEvent.event,
            enrichtedEvent
        );
        this.trackingHistory.push(enrichtedEvent);
    }

    private runHistory(callback: SubscriberCallback) {
        if (this.trackingHistory.length > 0) {
            // send history only for specific subscriber
            const predicate = (_eventName: string, _data: any, id: string) =>
                id === subscriber.id;
            const subscriber = this.mediator.subscribe(
                NAMESPACE_HISTORY,
                callback,
                { predicate }
            );

            this.trackingHistory.forEach((item) => {
                this.mediator.publish(
                    NAMESPACE_HISTORY,
                    item.event,
                    item,
                    subscriber.id
                );
            });

            this.mediator.remove(NAMESPACE_HISTORY, subscriber.id);
        }
    }
}

export default EventDataLayer;
